import { Dayjs } from "dayjs";
import { LloFunnelReportItem } from "../../../../api";

export type SortableColumn =
  | "caseName"
  | "raa"
  | "ap"
  | "upcomingDeadlines"
  | "lloExpiration"
  | "negotiation"
  | "funnel"
  | "dueDate"
  | "taskName"
  | "taskAssignee"
  | "description";

export type SortDirection = "asc" | "desc";

export type SortRule = {
  column: SortableColumn;
  direction: SortDirection;
};

export type HeadCell = {
  id: SortableColumn;
  label: string;
  /** Preferred share of table width (sums with other columns ≈ 100). */
  widthPct: number;
};

export type RowWithSpans = LloFunnelReportItem & {
  rowNumber?: number;
  groupRowSpan?: number;
  skipCaseColumns?: boolean;
  isLastInCaseGroup?: boolean;
};

export type LloFunnelFiltersState = {
  caseName: string;
  raa: string;
  ap: string;
  negotiation: string;
  funnel: string;
  taskName: string;
  taskAssignee: string;
  overdue: string;
  lloExpirationFrom: Dayjs | null;
  lloExpirationTo: Dayjs | null;
  dueDateFrom: Dayjs | null;
  dueDateTo: Dayjs | null;
};

export type FilterFieldKey = Exclude<
  keyof LloFunnelFiltersState,
  "dueDateFrom" | "dueDateTo" | "lloExpirationFrom" | "lloExpirationTo"
>;

export const EMPTY_FILTERS: LloFunnelFiltersState = {
  caseName: "",
  raa: "",
  ap: "",
  negotiation: "",
  funnel: "",
  taskName: "",
  taskAssignee: "",
  overdue: "",
  lloExpirationFrom: null,
  lloExpirationTo: null,
  dueDateFrom: null,
  dueDateTo: null,
};

export const FILTER_FIELD_LABELS: Record<FilterFieldKey, string> = {
  caseName: "Case",
  raa: "RAA",
  ap: "AP",
  negotiation: "Negotiation",
  funnel: "Funnel Type",
  taskName: "Task",
  taskAssignee: "Task Assignee",
  overdue: "Overdue",
};

/** Fixed funnel-category filter options (matches backend LloFunnelCategory / LloMainFunnels labels). */
export const MAIN_FUNNEL_STAGE_OPTIONS = [
  "Complaint",
  "Discovery Requests",
  "Plaintiff's Discovery Responses",
  "Judicial Assignment Responsive",
  "Case Management Conference",
] as const;

export type FunnelFilterOption = {
  group: string;
  label: string;
  value: string;
};

export const FUNNEL_FILTER_OPTIONS: FunnelFilterOption[] = [
  { group: "Main Funnel", label: "All", value: "Main Funnel — All" },
  ...MAIN_FUNNEL_STAGE_OPTIONS.map((stage) => ({
    group: "Main Funnel",
    label: stage,
    value: `Main Funnel — ${stage}`,
  })),
  { group: "Other", label: "Pre-Lit Funnel", value: "Pre-Lit Funnel" },
  { group: "Other", label: "Litigation Funnel", value: "Litigation Funnel" },
  { group: "Other", label: "Secondary Funnel", value: "Secondary Funnel" },
  { group: "Other", label: "Manual", value: "Manual" },
  { group: "Other", label: "None", value: "None" },
];

export const NEGOTIATION_FILTER_OPTIONS = ["Yes", "No"] as const;
export const OVERDUE_FILTER_OPTIONS = ["Yes", "No"] as const;

export const SORT_FIELD_OPTIONS: { value: SortableColumn; label: string }[] = [
  { value: "caseName", label: "Case Name" },
  { value: "raa", label: "RAA" },
  { value: "ap", label: "AP" },
  { value: "lloExpiration", label: "LLO Expiration" },
  { value: "negotiation", label: "Negotiation" },
  { value: "funnel", label: "Funnel" },
  { value: "dueDate", label: "Due Date" },
  { value: "taskName", label: "Task" },
  { value: "taskAssignee", label: "Task Assignee" },
  { value: "description", label: "Description" },
  { value: "upcomingDeadlines", label: "Upcoming Deadlines" },
];

export const SORT_DIRECTION_OPTIONS: { value: SortDirection; label: string }[] = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

export const DEFAULT_SORT_RULES: SortRule[] = [{ column: "caseName", direction: "asc" }];
